// src/lib/auth.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

interface User {
	id: string;
	email: string;
	// Add other user properties from your JWT payload
	exp: number;
}

interface AuthState {
	token: string | null;
	user: User | null;
	setToken: (token: string | null) => void;
	logout: () => void;
	isAuthenticated: () => boolean;
	getAuthHeader: () => string;
}

const useAuthStore = create<AuthState>()(
	persist(
		(set, get) => ({
			token: null,
			user: null,

			setToken: (token: string | null) => {
				if (token) {
					const user = jwtDecode<User>(token);
					set({ token, user });
				} else {
					set({ token: null, user: null });
				}
			},

			logout: () => {
				set({ token: null, user: null });
			},

			isAuthenticated: () => {
				const token = get().token;
				if (!token) return false;

				try {
					const decoded = jwtDecode<User>(token);
					return decoded.exp > Date.now() / 1000;
				} catch {
					return false;
				}
			},

			getAuthHeader: () => {
				const token = get().token;
				return token ? `Bearer ${token}` : '';
			}
		}),
		{
			name: 'token',
			storage: {
				getItem: (name: string) => {
					const str = sessionStorage.getItem(name);
					return str ? JSON.parse(str) : null;
				},
				setItem: (name: string, value: unknown) => {
					sessionStorage.setItem(name, JSON.stringify(value));
				},
				removeItem: (name: string) => sessionStorage.removeItem(name)
			}
		}
	)
);

// Custom hook to protect routes
export const useRequireAuth = () => {
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const navigate = useNavigate();

	useEffect(() => {
		if (!isAuthenticated()) {
			navigate('/login', { replace: true });
		}
	}, [isAuthenticated, navigate]);
};

// Type-safe hooks for consuming the store
export const useAuth = () => {
	const token = useAuthStore((state) => state.token);
	const user = useAuthStore((state) => state.user);
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const logout = useAuthStore((state) => state.logout);
	const setToken = useAuthStore((state) => state.setToken);
	const getAuthHeader = useAuthStore((state) => state.getAuthHeader);

	return {
		token,
		user,
		isAuthenticated,
		logout,
		setToken,
		getAuthHeader
	};
};

export default useAuthStore;
